import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  ViewChild,
  inject,
  input,
  isDevMode,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Auth,
  ConfirmationResult,
  User as FirebaseUser,
  PhoneAuthProvider,
  RecaptchaVerifier,
  Unsubscribe,
  getAuth,
  signInWithCredential,
  signInWithPhoneNumber,
  updateProfile,
} from '@angular/fire/auth';
import { FIREBASE_OPTIONS } from '@angular/fire/compat';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { IntroFlowWelcomeView } from '@components/intro-flow/welcome-view.component';
import { PhoneNumberInput } from '@components/phone-number-input/phone-number-input.component';
import { environment } from '@env/environment';
import { HttpPuebloClient } from '@http/http_pueblo_client';
import { HttpStoreFacade } from '@http/store/facade';
import { PuebloRouter } from '@routing/pueblo-router.service';
import { WindowService } from '@utils/window-service';
import phone from 'phone';
import { makeUserResourceName } from 'pueblo-types';
import {
  BehaviorSubject,
  delay,
  filter,
  firstValueFrom,
  interval,
  map,
  take,
  takeUntil,
} from 'rxjs';

const USER_CAP = 500;

enum Stage {
  WELCOME = 1,
  SIGN_IN = 2,
}

enum LoginState {
  NOT_STARTED = 10,
  PHONE_INPUT = 20,
  CODE_REQUESTED = 30,
  CODE_INPUT = 40,
  VERIFYING_CODE = 50,
  AUTH_COMPLETE = 60,
  NAME_CHOSEN = 70,
}

@Component({
  selector: 'login',
  templateUrl: 'login.component.html',
  styleUrl: 'login.component.scss',
  imports: [
    AsyncPipe,
    FormsModule,
    IntroFlowWelcomeView,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    PhoneNumberInput,
    ReactiveFormsModule,
  ],
  providers: [{ provide: FIREBASE_OPTIONS, useValue: environment.firebase }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Login implements AfterViewInit, OnDestroy {
  protected readonly Stage = Stage;
  protected readonly LoginState = LoginState;
  protected readonly isDevMode = isDevMode;

  private readonly fb = inject(FormBuilder);
  private readonly win = inject(WindowService);
  private readonly puebloRouter = inject(PuebloRouter);
  private readonly auth = inject(Auth);
  private readonly httpStore = inject(HttpStoreFacade);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly snackBar = inject(MatSnackBar);
  private readonly puebloClient = inject(HttpPuebloClient);
  private readonly destroyRef = inject(DestroyRef);

  contextText = input<string>('');
  loggedIn = output<void>();

  private readonly verifiedPhones$ = this.puebloClient.getVerifiedPhones();

  protected windowRef: any;
  protected verificationCode: string = '';
  protected user: any;
  protected verificationId: string = '';

  // Clean up.
  protected readonly authListeners: Array<PluginListenerHandle> = [];
  protected authChangedUnsubscribe: Unsubscribe | undefined = undefined;

  protected readonly validPhoneNumber$ = new BehaviorSubject<
    string | undefined
  >(undefined);
  protected readonly validVerificationCode$ = new BehaviorSubject<
    string | undefined
  >(undefined);
  protected readonly loginFailedMessage$ = new BehaviorSubject<string>('');
  protected readonly codeInputError$ = new BehaviorSubject<string>('');
  protected readonly confirmationResult$ = new BehaviorSubject<
    ConfirmationResult | undefined
  >(undefined);
  protected readonly loginState$ = new BehaviorSubject<LoginState>(
    LoginState.PHONE_INPUT,
  );
  protected $stage = signal(Stage.WELCOME);

  protected phoneNumberCtrl = this.fb.nonNullable.control<string>('');
  protected verificationCtrl = this.fb.nonNullable.control<string>('');

  @ViewChild('verifyInput', { read: ElementRef })
  private readonly verifyInput?: ElementRef;

  constructor() {
    this.phoneNumberCtrl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value: string) => {
        try {
          const number = phone(value);

          if (number.isValid) {
            this.validPhoneNumber$.next(number.phoneNumber);
          } else {
            this.validPhoneNumber$.next(undefined);
          }
        } catch (e) {
          this.validPhoneNumber$.next(undefined);
        }
      });

    this.verificationCtrl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value: string) => {
        this.codeInputError$.next('');
        if (value.length === 6) {
          this.validVerificationCode$.next(value);
        } else {
          this.validVerificationCode$.next(undefined);
        }
      });
    this.verificationCtrl.disable();

    // The phone may be smart enough to log in without the user having
    // to enter the code. This will navigate to home immediately if this
    // happens.
    // https://github.com/invertase/react-native-firebase/issues/530
    this.authChangedUnsubscribe = this.auth.onAuthStateChanged(
      (user: FirebaseUser | null) => {
        if (!user || user?.isAnonymous) return;

        // Force-fetches the app user every 1 second until the user is returned, and a name
        // has been set. This should prevent the app from ever hanging for too long.
        interval(1000)
          .pipe(
            delay(1000),
            takeUntil(
              this.httpStore.appUser$.pipe(
                filter((u) => !u.isAnon && !!u.stored?.displayName),
              ),
            ),
            takeUntilDestroyed(this.destroyRef),
            map(() =>
              this.httpStore.pingAppUser(makeUserResourceName(user.uid)),
            ),
          )
          .subscribe();

        this.completeFlow();
      },
    );
  }

  async ngAfterViewInit(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      this.authListeners.push(
        await FirebaseAuthentication.addListener(
          'phoneCodeSent',
          async (event) => {
            this.handleCodeSent(event.verificationId);
          },
        ),
      );

      this.authListeners.push(
        await FirebaseAuthentication.addListener(
          'phoneVerificationFailed',
          async (failedEvent) => {
            this.snackBar.open('Failed to send code, please retry.', 'Close', {
              duration: 2000,
            });
            this.loginState$.next(LoginState.PHONE_INPUT);
            this.loginFailedMessage$.next(failedEvent.message);
            this.phoneNumberCtrl.enable();
          },
        ),
      );
    }

    this.windowRef = this.win.windowRef;
    const auth = getAuth();
    this.windowRef.recaptchaVerifier = new RecaptchaVerifier(
      auth,
      'recaptcha-container',
      { size: 'invisible' },
    );

    this.windowRef.recaptchaVerifier.render();
  }

  ngOnDestroy() {
    for (const listener of this.authListeners) {
      listener.remove();
    }
    if (Capacitor.isNativePlatform()) {
      FirebaseAuthentication.removeAllListeners();
    }
  }

  reset() {
    this.$stage.set(Stage.SIGN_IN);
    this.loginState$.next(LoginState.PHONE_INPUT);
    this.phoneNumberCtrl.enable();
    this.phoneNumberCtrl.reset();
    this.verificationCtrl.disable();
    this.verificationCtrl.reset();
    this.codeInputError$.next('');
    this.loginFailedMessage$.next('');
  }

  protected handleBack() {
    this.loginState$.next(LoginState.PHONE_INPUT);
    this.verificationCtrl.disable();
    this.phoneNumberCtrl.enable();
  }

  protected async requestCode() {
    if (!this.validPhoneNumber$.value) return;

    const phoneNumber = this.validPhoneNumber$.value;

    const verifiedPhones = await firstValueFrom(this.verifiedPhones$);

    if (verifiedPhones.length >= USER_CAP) {
      await this.puebloRouter.goTo(['/beta']);
      return;
    }

    this.loginState$.next(LoginState.CODE_REQUESTED);
    this.loginFailedMessage$.next('');
    this.phoneNumberCtrl.disable();

    if (Capacitor.isNativePlatform()) {
      await FirebaseAuthentication.signInWithPhoneNumber({
        phoneNumber,
        timeout: 0,
      });
    } else {
      await signInWithPhoneNumber(
        this.auth,
        phoneNumber,
        this.windowRef.recaptchaVerifier,
      )
        .then((confirmationResult: ConfirmationResult) => {
          this.confirmationResult$.next(confirmationResult);
          this.handleCodeSent(confirmationResult.verificationId);
        })
        .catch(() => {
          this.snackBar.open('Failed to send code, please retry.', 'Close', {
            duration: 2000,
          });
          this.phoneNumberCtrl.enable();
          this.loginState$.next(LoginState.PHONE_INPUT);
        });
    }
  }

  protected slideTo(stage: Stage) {
    this.$stage.set(stage);
    this.changeDetector.detectChanges();
  }

  protected async submitCode() {
    await this.signInToWebLayer(this.validVerificationCode$.value ?? '');
  }

  private handleCodeSent(verificationId: string) {
    this.verificationId = verificationId;
    this.loginState$.next(LoginState.CODE_INPUT);
    this.verificationCtrl.enable();
    this.verifyInput?.nativeElement.focus();
  }

  protected completeFlow() {
    this.httpStore.appUser$
      .pipe(
        filter((u) => !u.isAnon),
        take(1),
      )
      .subscribe((appUser) => {
        this.loggedIn.emit();

        this.reset();

        // TODO: Remove this after all current users sign in after March 3rd
        if (
          this.auth.currentUser &&
          !this.auth.currentUser.displayName &&
          appUser.stored?.fullName &&
          this.userCreatedBeforeMarch3rd()
        ) {
          updateProfile(this.auth.currentUser, {
            displayName: appUser.stored.fullName,
          });
        }
      });
  }

  protected handleDevLogin(user: 'juanda' | 'new') {
    const generateRandomPhoneNumber = () => {
      let randomNumber = '';
      for (let i = 0; i < 10; i++) {
        const digit = (() => {
          let newNum = Math.floor(Math.random() * 10);
          while (i === 0 && newNum === 0) {
            newNum = Math.floor(Math.random() * 10);
          }
          return newNum;
        })();
        randomNumber += digit;
      }
      return randomNumber;
    };

    signInWithPhoneNumber(
      this.auth,
      user === 'juanda' ? '+17632830994' : `+1${generateRandomPhoneNumber()}`,
      new RecaptchaVerifier(this.auth, 'recaptcha-container', {
        size: 'invisible',
      }),
    ).then(async (confirmationResult) => {
      let code = '';
      await fetch(
        `http://${environment.firebase.localServer}:9099/emulator/v1/projects/${environment.firebase.options.projectId}/verificationCodes`,
      )
        .then(async (response) => {
          const data = await response.json();
          if (!data.verificationCodes || !data.verificationCodes.length) {
            throw new Error(
              'Failed to get verification code. Response: ' + data,
            );
          }
          code = data.verificationCodes[data.verificationCodes.length - 1].code;
        })
        .catch((error) => {
          throw new Error('Failed to get verification code: ' + error);
        });

      confirmationResult.confirm(code);
    });
  }

  private userCreatedBeforeMarch3rd(): boolean {
    return (
      Date.parse(this.auth.currentUser?.metadata.creationTime ?? '') <
      1741047126285
    );
  }

  private async signInToWebLayer(verificationCode: string) {
    if (!verificationCode || !this.verificationId) return;

    this.loginState$.next(LoginState.VERIFYING_CODE);

    const credential = PhoneAuthProvider.credential(
      this.verificationId,
      verificationCode,
    );

    await signInWithCredential(this.auth, credential)
      .then((cred) => {
        if (cred.user === null) {
          this.snackBar.open(
            'Invalid code, please check that you have provided the correct verification code.',
            'Close',
          );
          this.loginState$.next(LoginState.CODE_INPUT);
          this.codeInputError$.next('Invalid code, please retry.');
        } else {
          this.loginState$.next(LoginState.AUTH_COMPLETE);
        }
      })
      .catch((e) => {
        this.snackBar.open(`${e.code}: ${e.message}`, 'Close');
        this.loginState$.next(LoginState.CODE_INPUT);
        this.codeInputError$.next('Invalid code, please retry.');
      });
  }
}
